import React from 'react'
import styles from './ReportViewer.scss'
import { Editor, EditorState, CompositeDecorator, convertFromRaw } from 'draft-js'
import { JSONViewer } from './JSONViewer'
import 'draft-js/dist/Draft.css'
import { ImageViewer } from './ImageViewer'
import { GalleryViewer } from './GalleryViewer'
import { MapViewerContainer } from './MapViewer'
import { EmbedViewer } from './EmbedViewer'
import { TRouteMapViewer } from './TRouteMapViewer'
import { ClimbViewerContainer } from './ClimbViewer'
import appStyles from '../../App/appStyles.scss'
import { findDOMNode } from 'react-dom'
import { BlockLoading } from 'common/BlockLoading'
import _ from 'lodash'
import blockSizes from '../../App/blockSizes.scss'
import { getConsole } from 'lib/getConsole'
import { findLinkEntities, Link } from 'lib/draftUtils'
const console = getConsole('rr', 'ReportViewer')

const fromContent = (reportContent) => {
  return EditorState.createWithContent(convertFromRaw(reportContent), decorator)
}

class LazyLoad extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = { loadBlock: false }
    this._checkScrollPosThrottled = _.throttle(this.checkScrollPos, 100)
  }

  componentDidMount () {
    this.checkScrollPos()
    window.addEventListener('scroll', this._checkScrollPosThrottled)
  }

  componentWillUnmount () {
    window.removeEventListener('scroll', this._checkScrollPosThrottled)
  }

  checkScrollPos = () => {
    if (!this._ref) { return }
    const rect = findDOMNode(this._ref).getBoundingClientRect()
    if (rect.top < (window.innerHeight * 2)) { // preload 1 full screenful of content
      this.setState({ loadBlock: true })
    }
  }

  setRef = (ref) => {
    this._ref = ref
  }

  render () {
    const { loadBlock } = this.state
    const { height, children } = this.props
    return loadBlock
      ? <div>{children}</div>
      : <BlockLoading className={blockSizes.normalBlock} getRef={this.setRef} height={height | 400} />
  }
}

const LazyGallery = (props) => <LazyLoad><GalleryViewer {...props} /></LazyLoad>

const LazyMap = (props) => <LazyLoad height={420}><MapViewerContainer {...props} /></LazyLoad>

const LazyClimb = (props) => <LazyLoad height={200}><ClimbViewerContainer {...props} /></LazyLoad>

const blockRenderer = report => block => {
  const mappings = {
    image: ImageViewer,
    map: LazyMap,
    gallery: LazyGallery,
    embed: EmbedViewer,
    route: TRouteMapViewer,
    climb: LazyClimb,
    separator: () => <hr />,
    metablock: () => null
  }
  if (block.getType() === 'atomic') {
    return {
      component: mappings[block.getData().get('type')] || JSONViewer,
      editable: false, // very important - prevents crash when user enters text in custom component div
      props: {
        metricUnits: report.metricUnits
      }
    }
  }
  return null
}

const blockStyle = (contentBlock) => {
  const type = contentBlock.getType()
  const mappings = {
    'unstyled': appStyles.paragraph,
    'header-two': appStyles.heading
  }
  return mappings[type]
}

export class ReportViewer extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      editorState: props.report && fromContent(props.report.reportContent)
    }
  }

  componentDidUpdate (prevProps) {
    if ((prevProps.report && prevProps.report.id) !== (this.props.report && this.props.report.id)) {
      this.processReportContent()
    }
  }

  processReportContent = () => {
    console.log('‼️ processReportContent')
    this.setState({
      editorState: fromContent(this.props.report.reportContent)
    })
  }

  render () {
    const { report } = this.props
    const { editorState } = this.state
    if (!report || !editorState) { return null }
    return (
      <div className={styles.ReportViewer}>
        {report.reportContent && <Editor readOnly
          editorState={editorState}
          blockRendererFn={blockRenderer(report)}
          blockStyleFn={blockStyle}
        />}
      </div>
    )
  }
}

const findUrlsToLink = (contentBlock, callback) => {
  // original version: http://daringfireball.net/2010/07/improved_regex_for_matching_urls
  // using reformatted/js-friendly version from SO: http://stackoverflow.com/questions/6927719/url-regex-does-not-work-in-javascript
  const regexp = /\b((?:[a-z][\w-]+:(?:\/{1,3}|[a-z0-9%])|www\d{0,3}[.]|[a-z0-9.\-]+[.][a-z]{2,4}\/)(?:[^\s()<>]+|\(([^\s()<>]+|(\([^\s()<>]+\)))*\))+(?:\(([^\s()<>]+|(\([^\s()<>]+\)))*\)|[^\s`!()\[\]{};:'".,<>?«»“”‘’]))/gi // eslint-disable-line

  const text = contentBlock.getText()
  let matchArr, start
  while ((matchArr = regexp.exec(text)) !== null) {
    start = matchArr.index
    callback(start, start + matchArr[0].length)
  }
}

const Autolink = (props) => {
  const { decoratedText: url } = props
  return (
    <a href={url} target='_blank' rel='noopener'>
      {url}
    </a>
  )
}

const decorator = new CompositeDecorator([{
  strategy: findLinkEntities,
  component: Link
}, {
  strategy: findUrlsToLink,
  component: Autolink
}
])
